/**
 * AdHocTaskModal — create a lightweight "ad-hoc design task" card.
 *
 * Unlike "Draft with Rex", this generates NOTHING (no AI, no template, no
 * compose pipeline). It exists purely to track ad-hoc design work assigned to a
 * designer that doesn't otherwise flow through the system. The card moves
 * through the normal board stages like any other.
 *
 * Fields: title (required), brief, assigned designer, due date, priority, and an
 * OPTIONAL reference file (never a blocker — task is created even if the upload
 * fails). card_type = "ADHOC", category = "INTERNAL".
 */
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Loader2, ClipboardList, Paperclip, X } from "lucide-react";
import { toast } from "sonner";

const _API_BASE = (typeof window !== "undefined" && window.location.hostname !== "localhost")
  ? window.location.origin
  : (process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : ""));
const API = `${_API_BASE}/api`;

const PRIORITIES = [
  { value: 1, label: "P1 — highest", cls: "border-rose-400 bg-rose-50 text-rose-900" },
  { value: 2, label: "P2 — high", cls: "border-orange-400 bg-orange-50 text-orange-900" },
  { value: 3, label: "P3 — medium", cls: "border-sky-400 bg-sky-50 text-sky-900" },
  { value: 4, label: "P4 — low", cls: "border-slate-400 bg-slate-50 text-slate-700" },
];

export default function AdHocTaskModal({ open, onOpenChange, workspaceId, onCreated }) {
  const [title, setTitle] = useState("");
  const [brief, setBrief] = useState("");
  const [designer, setDesigner] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState(3);
  const [refFile, setRefFile] = useState(null);
  const [team, setTeam] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/users`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((u) => setTeam(Array.isArray(u) ? u : []))
      .catch(() => setTeam([]));
  }, [open]);

  const reset = () => {
    setTitle(""); setBrief(""); setDesigner(""); setDueDate(""); setPriority(3); setRefFile(null);
  };

  const submit = async () => {
    if (!title.trim()) { toast.error("Give the task a title"); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/cards`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          category: "INTERNAL",
          card_type: "ADHOC",
          designer_email: designer || null,
          body_md: brief.trim() || null,
          due_date: dueDate || null,
          priority,
        }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(typeof e?.detail === "string" ? e.detail : `Could not create task (${r.status})`);
      }
      const data = await r.json();
      const cardId = data?.card?.card_id;

      // Optional reference file — never blocks task creation.
      if (refFile && cardId) {
        try {
          const fd = new FormData();
          fd.append("file", refFile);
          const ur = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${cardId}/image`, {
            method: "POST", credentials: "include", body: fd,
          });
          if (!ur.ok) toast.warning("Task created, but the reference file upload failed.");
        } catch {
          toast.warning("Task created, but the reference file upload failed.");
        }
      }

      toast.success("Ad-hoc task created");
      reset();
      onOpenChange(false);
      onCreated && onCreated();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) { onOpenChange(v); if (!v) reset(); } }}>
      <DialogContent className="max-w-lg" data-testid="adhoc-task-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <ClipboardList className="h-5 w-5 text-teal-600" /> New ad-hoc design task
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <p className="text-xs text-slate-500">
            For tracking design work assigned to a designer that doesn't need the post pipeline. Nothing is generated — it just flows through the board.
          </p>

          <div className="space-y-1.5">
            <Label htmlFor="adhoc-title">Task title <span className="text-rose-600">*</span></Label>
            <Input
              id="adhoc-title" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Design a roll-up banner for the BOI summit"
              data-testid="adhoc-title-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="adhoc-brief">Brief / notes</Label>
            <Textarea
              id="adhoc-brief" value={brief} onChange={(e) => setBrief(e.target.value)} rows={3}
              placeholder="Describe what's needed, sizes, deadline context, links…"
              data-testid="adhoc-brief-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="adhoc-designer">Assign to</Label>
              <select
                id="adhoc-designer" value={designer} onChange={(e) => setDesigner(e.target.value)}
                className="w-full border border-slate-300 rounded-md h-10 px-2 text-sm bg-white"
                data-testid="adhoc-designer-select"
              >
                <option value="">Unassigned</option>
                {team.map((m) => (
                  <option key={m.email} value={m.email}>{m.name || m.email}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="adhoc-due">Due date</Label>
              <Input
                id="adhoc-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                data-testid="adhoc-due-input"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Priority</Label>
            <div className="flex gap-2">
              {PRIORITIES.map((p) => (
                <button
                  key={p.value} type="button" onClick={() => setPriority(p.value)}
                  className={`flex-1 text-xs font-semibold rounded-md border px-2 py-2 transition ${
                    priority === p.value ? p.cls + " ring-2 ring-offset-1 ring-slate-300" : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                  }`}
                  data-testid={`adhoc-priority-${p.value}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="adhoc-ref">Reference file <span className="text-slate-400 font-normal">(optional)</span></Label>
            {refFile ? (
              <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                <span className="flex items-center gap-2 truncate"><Paperclip className="h-4 w-4 text-slate-500" />{refFile.name}</span>
                <button type="button" onClick={() => setRefFile(null)} data-testid="adhoc-ref-clear" className="text-slate-400 hover:text-slate-700">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <Input
                id="adhoc-ref" type="file" accept="image/*"
                onChange={(e) => setRefFile(e.target.files?.[0] || null)}
                data-testid="adhoc-ref-input"
              />
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { onOpenChange(false); reset(); }} disabled={saving} data-testid="adhoc-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving || !title.trim()} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="adhoc-create">
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating…</> : "Create task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
